import { call, put, select, delay } from 'redux-saga/effects';
import * as api from '../../../../../util/api/RestaurantApiClient';
import * as order from '../../../reducers/ducks/legacy/OrderDuck';
import {
  checkoutErrorData,
  clearCheckoutRecovery,
  createCheckoutRecovery,
  persistCheckoutRecovery,
  readCheckoutRecovery,
  recoveryMessage,
} from '../../../../../util/checkoutIdentityRecovery';
import synchronizeDeliveryCheckout from './deliveryCheckoutSaga';
import { createCheckoutAttemptContext } from '../../../../../util/checkoutContext';
import { bindCheckoutTime } from '../../../reducers/ducks/new/Time/TimeReducer';
import { getOrderTiming } from '../../../../../util/orderTiming';
import {
  checkoutDispatchFailure,
  checkoutStatusError,
} from '../../../../../util/checkoutDispatchFailure';
import {
  readCheckoutIssuance,
  issuanceRecoveryMessage,
  retainIssuanceDiagnostic,
  recoverIssuanceSnapshot,
} from '../../../../../util/checkoutIssuanceRecovery';

function* remember(record) {
  yield call(persistCheckoutRecovery, record);
  yield put(order.setCheckoutRecovery(record));
}

const createIdentityGuard = (state) => {
  const context = createCheckoutAttemptContext(state);
  const key = context(state);
  let id = state.order.checkoutData?.id;
  return function* current(nextId, adopt = false) {
    const live = yield select((value) => value);
    if (context(live) !== key || live.order.checkoutData?.id !== id)
      throw new Error(recoveryMessage);
    if (adopt) id = nextId;
  };
};

// Only read-only recovery is retried automatically. No automatic new attempt,
// checkout replacement, charge, or uncertain completion replay is allowed here.
function* resolveExisting(initialRecord, current) {
  let record = initialRecord;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const identity = {};
      if (record.id) identity.checkout = record.id;
      else identity.start_attempt = record.request.start_attempt;
      const resolution = yield call(api.resolveCheckout, {
        client: record.request.client,
        ...identity,
      });
      yield call(current);
      if (record.id && resolution?.checkout_id !== record.id)
        throw new Error(recoveryMessage);
      if (resolution?.checkout_id) {
        record = { ...record, id: resolution.checkout_id };
        yield call(remember, record);
      }
      if (!resolution?.checkout_id || resolution.replacement_allowed !== false)
        throw new Error(recoveryMessage);
      return resolution;
    } catch (error) {
      yield call(current);
      const details = checkoutErrorData(error);
      if (record.id && details.checkout_id && record.id !== details.checkout_id)
        throw new Error(recoveryMessage);
      if (details.checkout_id) {
        record = { ...record, id: details.checkout_id };
        yield call(remember, record);
      }
      if (
        details.code === 'checkout_discovery_no_match' &&
        !record.discovery &&
        !record.id
      )
        return null;
      if (checkoutDispatchFailure(error))
        yield call(remember, {
          ...record,
          diagnostic: checkoutDispatchFailure(error),
        });
      if (checkoutDispatchFailure(error) || !details.retryable || attempt === 2)
        throw checkoutStatusError(error, recoveryMessage);
      yield delay((attempt + 1) * 250);
      yield call(current);
    }
  }
  throw new Error(recoveryMessage);
}

export function* adoptResolvedCheckout(resolution, client, current) {
  const state = yield select((value) => value);
  yield call(current);
  const existing = state.order.checkoutData;
  const checkout = {
    ...(existing?.id === resolution.checkout_id ? existing : {}),
    ...resolution.totals,
    id: resolution.checkout_id,
    restaurant: client,
    accountId: state.main.user?.id,
    delivery_revision: resolution.delivery_revision,
    recovered: true,
  };
  yield call(current, checkout.id, true);
  yield put(order.setCheckoutData(checkout));
  const synced = yield call(synchronizeDeliveryCheckout, {
    resolution,
    allowTakeout: true,
  });
  yield call(current);
  if (synced) {
    yield put(
      bindCheckoutTime({ id: checkout.id, key: getOrderTiming(state).key })
    );
    return true;
  }
  return false;
}

// Called before address/phone preparation when a recovery is already known.
export function* resumeCheckoutRecovery() {
  const state = yield select((value) => value);
  const issuance = readCheckoutIssuance(state);
  if (issuance) {
    const current = createIdentityGuard(state);
    let resolution;
    try {
      resolution = yield call(api.resolveCheckout, {
        client: issuance.client,
        checkout: issuance.checkoutId,
      });
    } catch (error) {
      yield call(current);
      yield call(retainIssuanceDiagnostic, state, error);
      throw checkoutStatusError(error, issuanceRecoveryMessage);
    }
    yield call(current);
    if (resolution?.checkout_id !== issuance.checkoutId)
      throw new Error(issuanceRecoveryMessage);
    const snapshot = recoverIssuanceSnapshot(state, issuance);
    if (snapshot) {
      yield call(current, issuance.checkoutId, true);
      yield put(order.setCheckoutData(snapshot));
    }
    yield put(
      order.setCheckoutDeliveryResult({ status: 'blocked', resolution })
    );
    return { handled: true, success: false };
  }
  const record = readCheckoutRecovery(state);
  if (!record) return { handled: false };
  const current = createIdentityGuard(state);
  const resolution = yield call(resolveExisting, record, current);
  if (resolution)
    return {
      handled: true,
      success: yield call(
        adoptResolvedCheckout,
        resolution,
        record.request.client,
        current
      ),
    };
  if (record.validationRejected) {
    // A definitive rejection AND authoritative no-match permit a corrected
    // request. Neither a timeout nor no-match alone permits rotating the key.
    yield call(clearCheckoutRecovery);
    yield put(order.setCheckoutRecovery(null));
    return { handled: false };
  }
  // Only a correlated no-match can replay creation. Use the exact saved request,
  // even if delivery/cart edits have since changed; synchronize them afterwards.
  return { handled: false, request: record.request };
}

export function* startCheckoutWithRecovery(request) {
  const state = yield select((value) => value);
  const current = createIdentityGuard(state);
  let record =
    readCheckoutRecovery(state) || createCheckoutRecovery(state, request);
  const replay = !!readCheckoutRecovery(state);
  yield call(remember, record);
  try {
    if (replay) {
      const { phone, ...data } = record.request.data;
      yield call(api.setOrderCart, { client: record.request.client, data });
      yield call(current);
    }
    const checkout = yield call(api.checkoutStart, record.request);
    yield call(current);
    if (!checkout?.id) throw new Error(recoveryMessage);
    record = { ...record, id: checkout.id };
    yield call(remember, record);
    return {
      checkout: { ...checkout, ...(replay ? { recovered: true } : {}) },
    };
  } catch (error) {
    yield call(current);
    const details = checkoutErrorData(error);
    if (checkoutDispatchFailure(error)) {
      yield call(remember, {
        ...record,
        diagnostic: checkoutDispatchFailure(error),
      });
      throw checkoutStatusError(error, recoveryMessage);
    }
    // A definitive validation rejection did not create a checkout. Retain the
    // request for status recovery, but do not expose internal codes to customers.
    if ([400, 422].includes(error?.status ?? error?.response?.status)) {
      yield call(remember, { ...record, validationRejected: true });
      throw error;
    }
    if (error?.data?.message?.toLowerCase().includes('had no address'))
      throw error;
    if (
      details.code === 'existing_checkout_requires_resolution' ||
      details.code === 'checkout_start_attempt_mismatch'
    ) {
      record = {
        ...record,
        discovery: true,
        ...(details.checkout_id ? { id: details.checkout_id } : {}),
      };
      yield call(remember, record);
    }
    const resolution = yield call(resolveExisting, record, current);
    if (!resolution) throw new Error(recoveryMessage);
    return {
      recovered: true,
      success: yield call(
        adoptResolvedCheckout,
        resolution,
        request.client,
        current
      ),
    };
  }
}
